/**
 * CLE-2811 — facts first: a FIRST generation's contract facts are saved while the pass is still running.
 *
 * clear-api stamps the run's `interimAt` when it persists them. From then until the pass ends, the latest
 * version holds the real `contract_term` recommendations — facts filled, reimbursement slots empty — and
 * the wizard shows them in place of the read-only previews, so she can read, correct and confirm the facts
 * while the worker is still reading each plan's schedule. When the pass completes the same recommendations
 * (same ids) get their slots filled, and a term she had confirmed flips back to `pending` with a
 * "Reimbursement schedule added…" `changeReason`: she re-confirms it with the schedule in view.
 *
 * Pure helpers, so each rule is tested without a DOM.
 */

import {
  DraftComponentType,
  DraftRunStage,
  DraftRunStatus,
  IDraftRecommendation,
  IWorkerProgress,
} from "../../../store/queries/contractModelDrafts";
import {
  PROGRESS_TITLE_FACTS,
  PROGRESS_TITLE_FULL,
  factsReadyTitle,
} from "../copy";
import { humanizeKey, isProcessing, recPayer } from "../utils";
import { FIELD_SPECS, fieldLabel } from "./fieldSpecs";
import {
  PreviewStatus,
  buildingSteps,
  partialPreviewOf,
} from "./partialPreview";
import { StepId } from "./steps";

// ---------------------------------------------------------------------------------------------
// The facts-first window
// ---------------------------------------------------------------------------------------------

/**
 * True while the run's pass has saved the contract facts and is still reading the schedules: it is
 * processing AND `interimAt` is set. A regeneration in flight (no `interimAt`) is not the window, and
 * neither is a finished run that still carries the stamp.
 */
export const isInterimRun = (run: {
  status: DraftRunStatus;
  interimAt?: string | null;
}): boolean => isProcessing(run.status) && !!run.interimAt;

// ---------------------------------------------------------------------------------------------
// The reimbursement slots on a term card
// ---------------------------------------------------------------------------------------------

/** A contract term's reimbursement slots, in form order: `reimbursement.inpatient`, `.outpatient`, … */
export const REIMBURSEMENT_SLOT_KEYS: string[] = FIELD_SPECS.contract_term
  .map((spec) => spec.key)
  .filter((key) => key.startsWith("reimbursement."));

const getPath = (obj: unknown, path: string): unknown =>
  path
    .split(".")
    .reduce<unknown>(
      (cur, part) =>
        cur && typeof cur === "object"
          ? (cur as Record<string, unknown>)[part]
          : undefined,
      obj
    );

/** Nothing in the slot yet: absent, null, or only whitespace. */
export const isEmptySlot = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");

export type ScheduleSlotState = "extracting" | "field";

/**
 * One reimbursement slot: "Extracting the schedule…" when it is empty inside the facts-first window,
 * otherwise the normal editable field — including an empty one once the pass has finished.
 */
export const scheduleSlotState = (
  value: unknown,
  run: { status: DraftRunStatus; interimAt?: string | null }
): ScheduleSlotState =>
  isInterimRun(run) && isEmptySlot(value) ? "extracting" : "field";

/** The slots of one recommendation to show as still extracting. Only a contract term has any. */
export const extractingSlotKeys = (
  componentType: DraftComponentType,
  modeling: unknown,
  run: { status: DraftRunStatus; interimAt?: string | null }
): string[] =>
  componentType === "contract_term"
    ? REIMBURSEMENT_SLOT_KEYS.filter(
        (key) => scheduleSlotState(getPath(modeling, key), run) === "extracting"
      )
    : [];

/** What a term card needs to render its extracting slots: the run's window, and its plan's status. */
export interface IScheduleExtraction {
  status: DraftRunStatus;
  interimAt: string | null;
  /** The worker's status for this term's plan; null when it reports none. */
  planStatus: PreviewStatus | null;
}

// ---------------------------------------------------------------------------------------------
// Where the worker is with the schedules
// ---------------------------------------------------------------------------------------------

interface IScheduleItem {
  name: string;
  payerId: number | null;
  status: PreviewStatus;
}

/**
 * The worker's word per plan. Its `terms` when it reports any — one per plan found, so a plan whose terms
 * it has not started counts as pending (`partialPreviewOf`) — otherwise the plans' own statuses.
 */
const scheduleItems = (progress: IWorkerProgress | null): IScheduleItem[] => {
  const preview = partialPreviewOf(progress);
  if (!preview) return [];
  const rawTerms = progress?.partial?.terms;
  const reportsTerms =
    Array.isArray(rawTerms) &&
    rawTerms.some(
      (t) => !!t && typeof t.name === "string" && t.name.trim() !== ""
    );
  return reportsTerms ? preview.terms : preview.plans;
};

export interface IScheduleCounts {
  done: number;
  total: number;
  failed: number;
}

/** How many plans' schedules are read ("N of M"), from `partial.terms`, else `partial.plans`. */
export const scheduleCounts = (
  progress: IWorkerProgress | null
): IScheduleCounts => {
  const items = scheduleItems(progress);
  return {
    done: items.filter((i) => i.status === "done").length,
    total: items.length,
    failed: items.filter((i) => i.status === "failed").length,
  };
};

/**
 * The progress strip's title: "Facts ready — extracting schedules N of M" in the facts-first window,
 * otherwise what the pass is — reading the facts, or building the whole model.
 */
export const progressStripTitle = (
  run: {
    status: DraftRunStatus;
    stage: DraftRunStage;
    interimAt?: string | null;
  },
  progress: IWorkerProgress | null
): string => {
  if (isInterimRun(run)) {
    const { done, total, failed } = scheduleCounts(progress);
    return factsReadyTitle(done, total, failed);
  }
  return run.stage === "facts" ? PROGRESS_TITLE_FACTS : PROGRESS_TITLE_FULL;
};

/**
 * The status to chip on a term's extracting slots: the worker's word for the term's payer, else for a
 * plan of the same name; null when the worker reports neither.
 */
export const planStatusFor = (
  rec: IDraftRecommendation,
  progress: IWorkerProgress | null
): PreviewStatus | null => {
  const items = scheduleItems(progress);
  if (!items.length) return null;
  const payer = recPayer(rec);
  if (payer.id != null) {
    const byId = items.find((i) => i.payerId === payer.id);
    if (byId) return byId.status;
  }
  const name = payer.name?.trim().toLowerCase();
  if (!name) return null;
  return (
    items.find((i) => i.name.trim().toLowerCase() === name)?.status ?? null
  );
};

// ---------------------------------------------------------------------------------------------
// "Schedule added — re-confirm"
// ---------------------------------------------------------------------------------------------

/**
 * How clear-api's `changeReason` starts on a term whose schedule arrived after she confirmed its facts
 * ("Reimbursement schedule added after you accepted the facts — please re-confirm"). Must match the API.
 */
export const SCHEDULE_ADDED_REASON_PREFIX = "Reimbursement schedule added";

/**
 * The amber "Schedule added — re-confirm" chip: that change put the term back to `pending`, and she has
 * not acted on it since. Once she confirms (or rejects) it again, the chip goes.
 */
export const showsScheduleAddedChip = (
  rec: Pick<IDraftRecommendation, "status" | "changeReason">
): boolean =>
  rec.status === "pending" &&
  typeof rec.changeReason === "string" &&
  rec.changeReason.trim().startsWith(SCHEDULE_ADDED_REASON_PREFIX);

// ---------------------------------------------------------------------------------------------
// "Updated: …" — a chat change that landed in place (CLE-2811)
// ---------------------------------------------------------------------------------------------

/** Fields whose parts have a form field each, but not the field itself ("escalator.percentPerYear", …). */
const UPDATED_FIELD_FALLBACK: Record<string, string> = {
  escalator: "Escalator",
  reimbursement: "Reimbursement",
};

/**
 * How one changed field reads on the "Updated" chip: the label its form field has on THIS recommendation
 * (`fieldLabel` over its component's specs — "reimbursement.inpatient" is "Inpatient reimbursement schedule"
 * on a hospital term, "Reimbursement schedule" on an ASC term's one slot), else a known group's name, else the
 * key in words (`humanizeKey`: "careSetting" → "Care Setting").
 */
export const updatedFieldLabel = (
  componentType: DraftComponentType,
  field: string,
  modeling: unknown
): string => {
  const spec = (FIELD_SPECS[componentType] ?? []).find((s) => s.key === field);
  const value =
    modeling && typeof modeling === "object" && !Array.isArray(modeling)
      ? (modeling as Record<string, unknown>)
      : null;
  if (spec) return fieldLabel(spec, value);
  return UPDATED_FIELD_FALLBACK[field] ?? humanizeKey(field);
};

/**
 * The labels of the amber "Updated: …" chip, or [] for no chip. clear-api marks a recommendation a change
 * moved as `modified` with the fields in `mergeDetails.changedFields` — a chat change applied in place, where
 * the same card (same id) now shows the new values, or a regeneration that moved it. The chip shows while it
 * is back to `pending`; once she confirms or rejects it, it goes.
 */
export const updatedChipLabels = (
  rec: Pick<
    IDraftRecommendation,
    | "status"
    | "changeKind"
    | "mergeDetails"
    | "componentType"
    | "proposedModeling"
  >
): string[] => {
  if (rec.status !== "pending" || rec.changeKind !== "modified") return [];
  const changed = rec.mergeDetails?.changedFields;
  const fields = Array.isArray(changed)
    ? changed.filter((f): f is string => typeof f === "string" && f !== "")
    : [];
  const labels = fields.map((f) =>
    updatedFieldLabel(rec.componentType, f, rec.proposedModeling)
  );
  return labels.filter((l, i) => labels.indexOf(l) === i);
};

// ---------------------------------------------------------------------------------------------
// The rail and the step bodies in the window
// ---------------------------------------------------------------------------------------------

/** The steps the facts-first window has real recommendations for. */
const READY_IN_WINDOW: StepId[] = ["segments", "terms"];

/**
 * The steps still being built (a spinner on the rail, a preview in the body). A first pass with nothing
 * saved: every content step (`buildingSteps`). The facts-first window: every content step except Lines of
 * Business and Contract Terms, which hold the saved facts — on a facts run, nothing.
 */
export const stepsStillBuilding = (
  visible: StepId[],
  recommendationCount: number,
  generating: boolean,
  interim: boolean
): StepId[] =>
  interim
    ? buildingSteps(visible, 0, generating).filter(
        (step) => !READY_IN_WINDOW.includes(step)
      )
    : buildingSteps(visible, recommendationCount, generating);

// ---------------------------------------------------------------------------------------------
// Keeping her edits when the schedules arrive
// ---------------------------------------------------------------------------------------------

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);

/** Deep equality for JSON-shaped values: key order does not matter, and a key holding undefined is absent. */
export const sameValue = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((v, i) => sameValue(v, b[i]))
    );
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    return Array.from(new Set([...Object.keys(a), ...Object.keys(b)])).every(
      (key) => sameValue(a[key], b[key])
    );
  }
  return false;
};

const rebaseValue = (
  base: unknown,
  theirs: unknown,
  ours: unknown
): unknown => {
  if (sameValue(ours, base)) return theirs;
  if (sameValue(theirs, base)) return ours;
  if (isPlainObject(base) && isPlainObject(theirs) && isPlainObject(ours)) {
    const merged: Record<string, unknown> = {};
    Array.from(
      new Set([
        ...Object.keys(base),
        ...Object.keys(theirs),
        ...Object.keys(ours),
      ])
    ).forEach((key) => {
      const value = rebaseValue(base[key], theirs[key], ours[key]);
      if (value !== undefined) merged[key] = value;
    });
    return merged;
  }
  // Both sides changed the same value: hers wins — she is mid-edit, and Save sends what she sees.
  return ours;
};

/**
 * A term card's form draft after the server sent the recommendation again, e.g. with its schedule filled
 * in: a three-way merge against the value the draft started from. Whatever she has not touched takes the
 * server's new value; whatever she edited keeps her edit. With no edits it is simply the server's value.
 *
 * When the merge comes out equal to the server's value — her edit just saved, say — it IS the server's
 * object, not a copy with its keys in another order (Postgres `jsonb` reorders them), so the card's
 * `JSON.stringify` dirty check reads clean again.
 */
export const rebaseDraft = (
  base: Record<string, unknown>,
  theirs: Record<string, unknown>,
  ours: Record<string, unknown>
): Record<string, unknown> => {
  const merged = rebaseValue(base, theirs, ours);
  if (!isPlainObject(merged) || sameValue(merged, theirs)) return theirs;
  return merged;
};
